/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  View,
  useWindowDimensions,
  LayoutChangeEvent,
  Platform,
  InteractionManager,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import BottomSheet from '@airasia-phoenix/react-native/components/BottomSheetView';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';

import { BottomSheetViewProps } from '../../types';
import { BOTTOM_SHEET_LAYOUT, BOTTOM_SHEET_SPACING } from './Constants';
import type { BottomSheetScrollViewMethods } from '@gorhom/bottom-sheet';

// Phoenix paints the backdrop differently per platform. On native it is Gorhom's
// BottomSheetBackdrop, whose opacity is derived from the sheet's animated index
// on every frame, so it is self-correcting. On web it is a plain Animated.View
// whose opacity comes from a reanimated shared value that Phoenix writes only
// when the sheet opens or closes — nothing re-derives it.
//
// Measured on mWeb: the first open paints that element at opacity 1, but on
// opens after a dismissal the animated style stops driving it and it is left
// wherever the fade happened to stop — sometimes 0, sometimes part-way, which is
// why the grey looked absent on some opens and washed out on others. Everything
// else about the element is correct — same size, position, stacking and colour —
// so the only thing to repair is the opacity. Phoenix and node_modules are off
// limits from this repo, hence this local, web-only repair.
//
// Remove once Phoenix derives the web backdrop's opacity from the sheet's
// animated index the way the native backdrop already does.
const TRANSPARENT_BACKGROUNDS = ['rgba(0, 0, 0, 0)', 'transparent'];
const BACKDROP_VIEWPORT_COVERAGE = 0.9;
// Phoenix fades to `backdropProps.opacity ?? 1`, and this wrapper never supplies
// backdropProps, so a healthy fade always ends at 1.
const BACKDROP_TARGET_OPACITY = 1;
// A live fade advances every frame, so a value that has not moved across this
// many consecutive frames means the animated style has stopped driving it.
const BACKDROP_STALLED_FRAMES = 3;
const BACKDROP_WATCH_TIMEOUT_MS = 1500;

// The backdrop is the only full-viewport positioned element carrying a
// non-transparent background, which identifies it without depending on
// Phoenix's generated class names.
const findWebBackdrops = (): HTMLElement[] => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return [];
  }

  const { innerWidth, innerHeight } = window;

  return Array.from(document.querySelectorAll<HTMLElement>('div')).filter(
    (element) => {
      const style = window.getComputedStyle(element);

      if (style.position !== 'absolute' && style.position !== 'fixed') {
        return false;
      }
      if (TRANSPARENT_BACKGROUNDS.includes(style.backgroundColor)) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return (
        rect.width >= innerWidth * BACKDROP_VIEWPORT_COVERAGE &&
        rect.height >= innerHeight * BACKDROP_VIEWPORT_COVERAGE
      );
    },
  );
};

const BottomSheetView = memo((props: BottomSheetViewProps) => {
  const {
    bottomSheetRef,
    title,
    onClose,
    children,
    height,
    stickyFooter,
    footerHeight,
    scrollViewProps,
    enablePanDownToClose = true,
    enableOverDrag = false,
    enableHandlePanningGesture = false,
    enableContentPanningGesture = Platform.OS !== 'web',
    initialHeight: initialHeightProp,
    finalHeight: finalHeightProp,
    shouldAutoScroll,
  } = props;

  const { height: screenHeight } = useWindowDimensions();
  const [contentHeight, setContentHeight] = useState(0);
  const scrollRef = useRef<BottomSheetScrollViewMethods>(null);

  const resolvedFooterHeight = stickyFooter
    ? (footerHeight ?? BOTTOM_SHEET_LAYOUT.DEFAULT_FOOTER_HEIGHT)
    : 0;

  const minHeight = screenHeight * 0.3;
  const maxHeight = screenHeight * 0.8;
  const headerEstimate = 30;

  const handleClose = useCallback(() => {
    bottomSheetRef.current?.toggleBottomSheetView?.();
    onClose();
  }, [bottomSheetRef, onClose]);

  const backdropWatchFrame = useRef<number | null>(null);

  const stopBackdropWatch = useCallback(() => {
    if (backdropWatchFrame.current !== null) {
      cancelAnimationFrame(backdropWatchFrame.current);
      backdropWatchFrame.current = null;
    }
  }, []);

  // Watch from the frame the sheet reports itself open, rather than waiting out a
  // fixed delay, so a stalled backdrop is repaired within a few frames instead of
  // popping in visibly late. The fade is only ever left alone once it reaches its
  // target — a fade that stops short is finished off at whatever value it froze.
  const startBackdropWatch = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    stopBackdropWatch();

    const startedAt = Date.now();
    let previousOpacity: string | null = null;
    let stalledFrames = 0;

    const inspectBackdrops = () => {
      const backdrops = findWebBackdrops();
      const timedOut = Date.now() - startedAt > BACKDROP_WATCH_TIMEOUT_MS;

      if (backdrops.length > 0) {
        const currentOpacity = window.getComputedStyle(backdrops[0]).opacity;

        if (Number(currentOpacity) >= BACKDROP_TARGET_OPACITY) {
          backdropWatchFrame.current = null;
          return;
        }

        stalledFrames =
          currentOpacity === previousOpacity ? stalledFrames + 1 : 0;
        previousOpacity = currentOpacity;

        if (stalledFrames >= BACKDROP_STALLED_FRAMES || timedOut) {
          backdrops.forEach((backdrop) =>
            backdrop.style.setProperty(
              'opacity',
              String(BACKDROP_TARGET_OPACITY),
            ),
          );
          backdropWatchFrame.current = null;
          return;
        }
      } else if (timedOut) {
        // Sheet never produced a backdrop; stop rather than spin forever.
        backdropWatchFrame.current = null;
        return;
      }

      backdropWatchFrame.current = requestAnimationFrame(inspectBackdrops);
    };

    backdropWatchFrame.current = requestAnimationFrame(inspectBackdrops);
  }, [stopBackdropWatch]);

  useEffect(() => stopBackdropWatch, [stopBackdropWatch]);

  const handleSheetStateChange = useCallback(
    (visible: boolean) => {
      if (visible) {
        startBackdropWatch();
      } else {
        stopBackdropWatch();
      }
    },
    [startBackdropWatch, stopBackdropWatch],
  );

  const onLayoutContent = useCallback((event: LayoutChangeEvent) => {
    const measuredHeight = event?.nativeEvent?.layout?.height ?? 0;
    if (measuredHeight > 0) {
      setContentHeight(measuredHeight);
    }
  }, []);

  const onContentSizeChange = useCallback(
    (_: number, h: number) => {
      // 1. Calculate if the sheet was ALREADY at max height before this new room was added.
      // We use the existing `contentHeight` state (which hasn't been updated to `h` yet).
      const prevTotalNeeded =
        contentHeight + resolvedFooterHeight + headerEstimate;
      const wasAlreadyAtMaxHeight = prevTotalNeeded >= maxHeight;

      setContentHeight(h);

      if (!shouldAutoScroll?.current) {
        return;
      }

      shouldAutoScroll.current = false;

      InteractionManager.runAfterInteractions(() => {
        // 2. We enable the animation ONLY if the bottom sheet has stopped expanding
        // and is fixed at its max height. This prevents scroll layout collisions
        // on both iOS and Android during the expansion phase (e.g., Room 4).
        const shouldAnimateScroll = wasAlreadyAtMaxHeight;

        scrollRef.current?.scrollToEnd({
          animated: shouldAnimateScroll,
        });
      });
    },
    [
      shouldAutoScroll,
      contentHeight,
      resolvedFooterHeight,
      headerEstimate,
      maxHeight,
    ],
  );

  const computedHeight = useMemo(() => {
    if (contentHeight > 0) {
      const totalNeeded = contentHeight + resolvedFooterHeight + headerEstimate;
      const dynamicHeight = Math.min(totalNeeded, maxHeight);
      return Math.max(minHeight, dynamicHeight);
    }

    const fallbackHeight = height ?? BOTTOM_SHEET_LAYOUT.DEFAULT_HEIGHT;
    const adjustedFallback = stickyFooter
      ? fallbackHeight + resolvedFooterHeight + headerEstimate
      : fallbackHeight;
    return Math.min(maxHeight, Math.max(adjustedFallback, minHeight));
  }, [
    contentHeight,
    resolvedFooterHeight,
    headerEstimate,
    minHeight,
    maxHeight,
    height,
    stickyFooter,
  ]);

  const contentContainerStyle = useMemo(() => {
    return Object.assign(
      {},
      styles.scrollContent,
      {
        backgroundColor: getColor(pd.sys.colour.container_none),
      },
      scrollViewProps?.contentContainerStyle,
    );
  }, [scrollViewProps?.contentContainerStyle, styles.scrollContent]);

  return (
    <BottomSheet
      ref={bottomSheetRef}
      initialHeight={initialHeightProp ?? computedHeight}
      finalHeight={finalHeightProp ?? computedHeight}
      showHeader
      showBackdrop
      onCloseIconPress={handleClose}
      onBottomSheetClose={onClose}
      onStateChange={handleSheetStateChange}
      sectionTitle={title}
      closeIconProps={{ icon: pd.icon.main.navigation.close }}
      enablePanDownToClose={enablePanDownToClose}
      enableOverDrag={enableOverDrag}
      enableHandlePanningGesture={enableHandlePanningGesture}
      enableContentPanningGesture={enableContentPanningGesture}
      enableDynamicSize={!stickyFooter}
    >
      <BottomSheetScrollView
        ref={scrollRef}
        testID="BottomSheetScrollView"
        {...scrollViewProps}
        onContentSizeChange={onContentSizeChange}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled={true}
        style={[
          {
            minHeight:
              contentHeight > 0
                ? undefined
                : (height ?? BOTTOM_SHEET_LAYOUT.DEFAULT_HEIGHT),
            maxHeight: maxHeight - resolvedFooterHeight - headerEstimate,
          },
          scrollViewProps?.style,
        ]}
        contentContainerStyle={contentContainerStyle}
        scrollEnabled={true}
        showsVerticalScrollIndicator={false}
      >
        <View onLayout={onLayoutContent}>{children}</View>
      </BottomSheetScrollView>

      {stickyFooter && <View style={styles.footer}>{stickyFooter}</View>}
    </BottomSheet>
  );
});

const styles = StyleSheet.create(() => ({
  scrollContent: {
    paddingHorizontal: BOTTOM_SHEET_SPACING.HORIZONTAL,
    paddingTop: BOTTOM_SHEET_SPACING.TOP,
    backgroundColor: BOTTOM_SHEET_LAYOUT.BG_COLOR,
  },
  footer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 4,
    paddingRight: 4,
    backgroundColor: BOTTOM_SHEET_LAYOUT.BG_COLOR,
  },
}));

BottomSheetView.displayName = 'BottomSheetView';

export default BottomSheetView;
